import { useId, type ReactElement } from "react";
import type { WildKind } from "@fiveout/core";

/**
 * The four wild emblems from the basic decks' card faces, as SVG for the setup
 * screen's wild buttons.
 *
 * The geometry is `pixi/cardArt.ts`'s (`smashEmblem`, `lightningEmblem`,
 * `swapEmblem`, `peekEmblem`), ported number for number in the same emblem-local
 * units centered on 0,0, so a button and the card it switches on are recognisably
 * the same mark. Two things differ, both because a button is ~30px where a card
 * emblem is ~90 units drawn at card size:
 *
 * - strokes are roughly doubled, or the cracks and arcs vanish below a pixel;
 * - the card's outermost reach (Smash's longest cracks) is pulled in a little,
 *   so the mark fills the button rather than floating small inside its spread.
 *
 * And it is ENGRAVED rather than printed, like the home menu's glyphs
 * (`MenuIcon`): the same shapes twice, a light pass one unit low and a dark pass
 * on top, so the mark reads as cut into the button - brass when the kind is on,
 * the plain chip when it is off - instead of a colored sticker on it. If the
 * card emblems change, change these with them.
 */

type Pt = readonly [number, number];

const polar = (deg: number, r: number): Pt => [Math.cos((deg * Math.PI) / 180) * r, Math.sin((deg * Math.PI) / 180) * r];
const poly = (pts: readonly Pt[]): string => `M${pts.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join("L")}Z`;
/** `place` from cardArt: a flat point list scaled and offset. */
const place = (flat: readonly number[], s = 1, ox = 0, oy = 0): Pt[] => {
  const out: Pt[] = [];
  for (let i = 0; i < flat.length; i += 2) out.push([ox + flat[i] * s, oy + flat[i + 1] * s]);
  return out;
};

// ---- Smash: an uneven impact star, fracture lines and thrown shards ----------
const SMASH_RADII = [26, 9, 21, 8.5, 27, 10, 19, 8, 24, 9.5, 22, 8.5];
const burst = (scale: number, ox: number, oy: number): Pt[] =>
  SMASH_RADII.map((r, i) => {
    const [x, y] = polar((360 * i) / SMASH_RADII.length - 90, r * scale);
    return [ox + x, oy + y] as Pt;
  });
const SMASH_CRACKS = [-118, -62, -18, 22, 74, 128, 168, -158].map((deg, i) => {
  const kink = i % 2 === 0 ? 16 : -14; // cardArt's 0.28 / -0.24 rad
  const inner = 17;
  const outer = i % 2 === 0 ? 38 : 33; // card: 46 / 38
  const mid = (inner + outer) / 2;
  const [a, b, c] = [polar(deg, inner), polar(deg + kink, mid), polar(deg, outer)];
  return { d: `M${a[0]} ${a[1]}L${b[0]} ${b[1]}L${c[0]} ${c[1]}`, major: i % 2 === 0 };
});
const SHARD = [0, -3.4, 3.2, -0.6, 1, 3.4, -2.6, 1.2];
const SMASH_DEBRIS: [number, number, number][] = [
  [-27, -22, 1], [25, -19, 0.8], [30, 12, 0.95], [-24, 18, 0.7], [4, 31, 0.85], [-33, 2, 0.6],
];

function smash(): ReactElement {
  return (
    <>
      {SMASH_CRACKS.map((c, i) => (
        <path key={i} d={c.d} fill="none" strokeWidth={c.major ? 2.2 : 1.5} />
      ))}
      <path d={poly(burst(1, 0, 0))} data-solid />
      <path d={poly(burst(0.5, -1.4, -2))} data-facet />
      {SMASH_DEBRIS.map(([dx, dy, s], i) => (
        <path key={i} d={poly(place(SHARD, s * 1.3, dx, dy))} data-solid />
      ))}
    </>
  );
}

// ---- Lightning: a solid zigzag bolt, arcs to each side, sparks ---------------
const BOLT = [5, -25, -11, 2, -1.5, 2, -7, 25, 12, -3, 2.5, -3];
const LIGHTNING_SPARKS: [number, number, number][] = [
  [-24, -18, 1.6], [22, -22, 1.2], [29, 4, 1.5], [-29, 6, 1.2], [16, 24, 1.4], [-15, 27, 1.1],
];

function lightning(): ReactElement {
  return (
    <>
      {[-1, 1].map((dir) => (
        <path key={dir} d={`M${dir * 15} -12L${dir * 26} -6L${dir * 20} -1L${dir * 33} 6`} fill="none" strokeWidth={2.4} />
      ))}
      <path d={poly(place(BOLT, 1.06))} data-solid />
      <path d={poly(place(BOLT, 0.62, -1.6, -1.2))} data-facet />
      {LIGHTNING_SPARKS.map(([x, y, r], i) => (
        <circle key={i} cx={x} cy={y} r={r * 1.5} data-solid />
      ))}
    </>
  );
}

// ---- Swap: two S-curve arrows crossing, one passing over the other -----------
const swapCurve = (dir: 1 | -1) => `M${-dir * 19} -15C${-dir * 1} -15 ${dir * 1} 13 ${dir * 15} 13`;
const swapHead = (dir: 1 | -1): Pt[] => {
  const head = place([0, -7.5, 11, 0, 0, 7.5], 1, dir * 13, 13);
  // Mirrored in place for the arrow travelling left, as cardArt does.
  return dir < 0 ? head.map(([x, y]) => [2 * (dir * 13) - x, y] as Pt) : head;
};
const SWAP_FLECKS: [number, number, number][] = [
  [-27, -19, 1.5], [-30, -11, 1.1], [27, -19, 1.5], [30, -11, 1.1], [-6, 27, 1.2], [6, 27, 1.2],
];

function swap(gapMaskId: string): ReactElement {
  return (
    <>
      {/* The card draws a band of card stock round the arrow on top; on metal
          there is no flat color to paint that band in, so the arrow underneath
          is masked instead and the button itself shows through the gap. */}
      <g mask={`url(#${gapMaskId})`}>
        <path d={swapCurve(-1)} fill="none" strokeWidth={5.6} />
        <path d={poly(swapHead(-1))} data-solid />
      </g>
      <path d={swapCurve(1)} fill="none" strokeWidth={5.6} />
      <path d={poly(swapHead(1))} data-solid />
      {SWAP_FLECKS.map(([x, y, r], i) => (
        <circle key={i} cx={x} cy={y} r={r * 1.5} data-solid />
      ))}
    </>
  );
}

// ---- Peek: an open eye under a fan of lashes ----------------------------------
/** `eyeShape` from cardArt: the almond, `w` its half-width. */
const almond = (cy: number, w: number): string => {
  const h = w * 0.62;
  return `M${-w} ${cy}C${-w * 0.5} ${cy - h} ${w * 0.5} ${cy - h} ${w} ${cy}C${w * 0.5} ${cy + h} ${-w * 0.5} ${cy + h} ${-w} ${cy}Z`;
};
const circlePath = (cx: number, cy: number, r: number) => `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 ${-r * 2} 0Z`;
const PEEK_LASHES = Array.from({ length: 5 }, (_, i) => {
  const deg = -150 + i * 30;
  const a = (deg * Math.PI) / 180;
  return `M${Math.cos(a) * 21} ${2 + Math.sin(a) * 17}L${Math.cos(a) * 29} ${2 + Math.sin(a) * 25}`;
});

function peek(): ReactElement {
  return (
    <>
      {PEEK_LASHES.map((d, i) => (
        <path key={i} d={d} fill="none" strokeWidth={3} />
      ))}
      {/* The lid as a ring - the white of the eye is the button showing through. */}
      <path d={`${almond(2, 27)}${almond(2, 21)}`} fillRule="evenodd" data-solid />
      {/* Iris with the pupil cut out of it, and the pupil back in, smaller: the
          ring between them is what the card's lighter iris reads as. */}
      <path d={`${circlePath(0, 2, 10)}${circlePath(0, 2, 5.6)}`} fillRule="evenodd" data-solid />
      <circle cx={0} cy={2} r={3.6} data-solid />
      <circle cx={-3} cy={-1.4} r={2.1} data-facet />
    </>
  );
}

/** Wide enough for every emblem's reach, centered on the emblem's origin. */
const VIEW_BOX = "-40 -32 80 66";

export function WildIcon({ kind }: { kind: WildKind }) {
  const gapMaskId = `wild-swap-gap-${useId().replace(/:/g, "")}`;
  const glyph =
    kind === "smash" ? smash() : kind === "lightning" ? lightning() : kind === "swap" ? swap(gapMaskId) : peek();
  return (
    <svg className="wild-icon" viewBox={VIEW_BOX} aria-hidden="true" focusable="false">
      {kind === "swap" && (
        <defs>
          <mask id={gapMaskId} maskUnits="userSpaceOnUse" x="-40" y="-32" width="80" height="66">
            <rect x="-40" y="-32" width="80" height="66" fill="#fff" />
            <path d={swapCurve(1)} fill="none" stroke="#000" strokeWidth={11} strokeLinecap="round" />
          </mask>
        </defs>
      )}
      <g className="engrave-light" transform="translate(0 1.6)">
        {glyph}
      </g>
      <g className="engrave-shadow">{glyph}</g>
    </svg>
  );
}
